import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { focusValidationError } from '../../../../lib/validationFocus';
import { repositoryApi } from '../repositoryApi';
import type { RepositoryItemListItem } from '../repositoryTypes';

/** PDF only, 50 MB — the repository-documents category, same as an upload. */
const MAX_BYTES = 50 * 1024 * 1024;

/** The ceiling the server's validator enforces. A guard against a typo, not a rule. */
const MAX_INCREMENT = 50;

/**
 * Supersedes a document's file and advances its version — the DIM's revision path (§6.2).
 *
 * **Not an overwrite, and the form says so before the tick.** The current file is archived into
 * the document's history at the number it carried; the document advances. The reference number
 * does not move: a revision is the same document, which is why that number is permanent.
 *
 * **The replacement PDF is OPTIONAL**, which is the reference's behaviour rather than an
 * omission: a revision to the record — a re-issue under a change request, a correction that
 * does not touch the file — advances the version and keeps the same bytes.
 *
 * **No Autogenerate.** The one field it could fill is a PDF whose format the server verifies by
 * magic bytes, so a generator could only produce a payload the upload would refuse — the rule
 * says the prop is omitted and no button renders at all.
 */
export function IncrementVersionModal({
  open,
  document,
  onClose,
  onSaved,
}: {
  open: boolean;
  document: RepositoryItemListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [increment, setIncrement] = useState('1');
  const [file, setFile] = useState<File | null>(null);
  const [changeDescription, setChangeDescription] = useState('');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const parsed = Number.parseInt(increment, 10);
  const incrementValid = Number.isInteger(parsed) && parsed > 0 && parsed <= MAX_INCREMENT;
  const tooLarge = file !== null && file.size > MAX_BYTES;

  const valid = incrementValid && !tooLarge;
  const canSave = valid && confirmed && !saving;

  const nextVersion = incrementValid ? document.versionNumber + parsed : null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await repositoryApi.replace(document.id, {
        increment: parsed,
        changeDescription: changeDescription.trim() || null,
        file,
      });

      onSaved();
      onClose();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not revise this document.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'repository-version' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Increment version"
      size="md"
      id="repository-version-modal"
      confirm={{
        id: 'repository-version-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: !valid || saving,
      }}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="repository-version-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
            leading={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
          >
            Submit
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error.message}
          </p>
        ) : null}

        {/*
          The consequence, stated in the BODY where it can be read before the tick rather than
          appended to the thing the reader is already clicking past. It names both numbers,
          because "increment" on its own does not say what the document becomes.
        */}
        <p className="text-sm text-slate-700">
          The current file{' '}
          <span className="font-medium text-slate-900">v{document.versionNumber}</span> is
          archived into this document&apos;s history, and it becomes{' '}
          <span className="font-medium text-slate-900">
            {nextVersion === null ? '—' : `v${nextVersion}`}
          </span>
          . Its reference number does not change: a revision is the same document.
        </p>

        <TextField
          id="repository-version-increment"
          label="Version increment"
          required
          type="number"
          min={1}
          max={MAX_INCREMENT}
          value={increment}
          onChange={(event) => setIncrement(event.target.value)}
          description="How many versions to advance. One for an ordinary revision — more only to reconcile a document revised outside the system."
          error={
            increment.trim() === ''
              ? 'Enter how many versions to advance.'
              : !incrementValid
                ? `Advance by between 1 and ${MAX_INCREMENT} versions.`
                : error?.fieldError('increment')
          }
        />

        <div>
          <label
            htmlFor="repository-version-file"
            className="mb-1.5 block text-sm font-medium text-slate-700"
          >
            Replacement PDF
          </label>

          <input
            id="repository-version-file"
            name="repository-version-file"
            type="file"
            accept="application/pdf,.pdf"
            disabled={saving}
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            className="block w-full rounded-lg border border-slate-300 bg-white text-sm text-slate-600 file:mr-3 file:cursor-pointer file:rounded-l-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200 disabled:cursor-not-allowed disabled:bg-slate-50"
          />

          {/*
            OPTIONAL, and the help text says what happens without one rather than leaving the
            reader to guess that an empty file input means "keep the current file".
          */}
          <p className="mt-1.5 text-xs text-slate-500">
            Optional. Without one the document keeps its current file and only the version
            advances. PDF only, up to {formatBytes(MAX_BYTES)}.
          </p>

          {tooLarge ? (
            <p className="mt-1 text-xs text-red-700">
              That file is {formatBytes(file.size)}, over the {formatBytes(MAX_BYTES)} limit.
            </p>
          ) : null}
        </div>

        <TextField
          id="repository-version-change-description"
          label="Change description"
          value={changeDescription}
          onChange={(event) => setChangeDescription(event.target.value)}
          description="What changed in this version. Recorded against the file being archived, so the history reads as a revision log. Where a change request governs the revision, reference it here."
          error={error?.fieldError('changeDescription')}
        />
      </div>
    </Modal>
  );
}
